import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomModal, StyledTextInput } from 'components/shared';
import { CustomModalProps } from 'components/shared/CustomModal/CustomModal';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useForm } from 'hooks/useForm';
import { useModal } from 'hooks/useModal';
import { HTTPService } from 'service';
import { logNetworkError } from 'utils/logNetworkError';
import { isNonEmpty } from 'utils/validation';

import styles from './BlockClientModal.module.css';

type BlockClientModalProps = CustomModalProps & {
   selectedUserId: string;
   onSuccessBlockClient: (userId: string) => void;
};

const INITIAL_STATE = { comment: '' };

const BlockClientModal = ({
   selectedUserId,
   onSuccessBlockClient,
   onClose,
   ...props
}: BlockClientModalProps) => {
   const { t } = useTranslation();
   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const validations = [
      ({ comment }: { comment: string }) =>
         !isNonEmpty(comment) && { comment: t('modals.block_client.validation_required') },
   ];

   const handleBlockClient = () => {
      setIsLoading(true);
      HTTPService.changeUserBlockedStatus(true, values.comment, selectedUserId)
         .then(() => {
            onSuccessBlockClient(selectedUserId);
            closeModalHandler();
         })
         .catch(error => {
            setErrorInfo(DEFAULT_ERROR_DESCRIPTIONS.UNKNOWN);
            logNetworkError(error);
         })
         .finally(() => setIsLoading(false));
   };

   const { values, errorsList, touched, changeHandler, submitHandler, clearForm } = useForm({
      initialState: INITIAL_STATE,
      validations,
      onSubmit: handleBlockClient,
   });

   const handleCloseModal = useCallback(() => {
      clearForm();
      onClose();
   }, [clearForm, onClose]);

   const {
      isLoading,
      errorInfo,
      showCloseWarning,
      setIsLoading,
      setErrorInfo,
      handleHideCloseWarning,
      handleShowCloseWarning,
      closeModalHandler,
   } = useModal({ onClose: handleCloseModal });

   return (
      <CustomModal
         {...props}
         onClose={handleShowCloseWarning}
         icon="BAN"
         title={t('modals.block_client.title')}
         error={errorInfo}
         leftSideModalContent
         isLoading={isLoading}
         onSubmit={submitHandler}
         primaryButtonProps={{
            text: t('modals.block_client.title'),
            variant: 'filled-danger',
         }}
         secondaryButtonProps={{
            text: t('common.cancel'),
            variant: 'outlined-danger',
            onClick: handleShowCloseWarning,
         }}
         closeWarningModalProps={{
            opened: showCloseWarning,
            onClose: handleHideCloseWarning,
            onConfirm: closeModalHandler,
         }}
      >
         <>
            <p className={styles.description}>
               {t('modals.block_client.description_line_1')}
               <br />
               {t('modals.block_client.description_line_2')}
            </p>
            <StyledTextInput
               type="text"
               name="comment"
               label={t('modals.block_client.comment_label')}
               value={values.comment}
               onChange={changeHandler}
               placeholder={t('modals.block_client.comment_placeholder')}
               error={touched.comment && errorsList.comment}
               helperText={touched.comment && errorsList.comment}
               fullWidth
            />
         </>
      </CustomModal>
   );
};

export default BlockClientModal;
